import { useState } from 'react'
import { sitesApi } from '../utils/api'

// ── §95 Client Handoff — preview link + approval workflow ─────────────────────

interface Props {
  siteId: string
  siteName: string
  siteSlug: string
  onClose: () => void
}

type ApprovalStatus = 'pending' | 'approved' | 'changes_requested'

export function ClientHandoffModal({ siteId, siteName, siteSlug, onClose }: Props) {
  const [copied, setCopied] = useState(false)
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [status, setStatus] = useState<ApprovalStatus>('pending')
  const [approvalNote, setApprovalNote] = useState('')

  const previewUrl = `${window.location.origin}/s/${siteSlug}`
  const reviewUrl = `${previewUrl}?review=1`

  function copyLink() {
    navigator.clipboard.writeText(reviewUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  async function sendReviewEmail() {
    if (!email.trim()) return
    setSending(true)
    try {
      await sitesApi.sendClientReview(siteId, {
        clientEmail: email.trim(),
        note: note.trim() || undefined,
        previewUrl: reviewUrl,
      })
      setSent(true)
      setEmail('')
      setNote('')
    } catch {
      alert('Не удалось отправить письмо. Скопируйте ссылку вручную.')
    } finally {
      setSending(false)
    }
  }

  const STATUS_LABELS: Record<ApprovalStatus, string> = {
    pending: 'Ожидает проверки',
    approved: 'Одобрено клиентом',
    changes_requested: 'Запрошены правки',
  }

  const STATUS_COLORS: Record<ApprovalStatus, string> = {
    pending: '#f59e0b',
    approved: '#22c55e',
    changes_requested: '#ef4444',
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 540, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>🤝 Client Handoff</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{siteName} · §95</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Preview link */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Ссылка для клиента</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={reviewUrl}
                style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-2)', color: 'var(--text-secondary)', fontFamily: 'monospace' }}
                onFocus={e => e.target.select()}
              />
              <button
                onClick={copyLink}
                style={{ padding: '8px 14px', background: copied ? '#22c55e' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}
              >
                {copied ? '✓ Скопировано' : '⎘ Копировать'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Откроет сайт в режиме просмотра. Клиент видит готовый сайт без редактора.
            </div>
          </div>

          {/* Send via email */}
          <div style={{ padding: '16px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Отправить на email</div>
            {sent ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#22c55e', fontSize: 13, fontWeight: 600 }}>
                ✓ Письмо отправлено! Клиент получит ссылку на предпросмотр.
              </div>
            ) : (
              <>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="client@example.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-1)', color: 'var(--text-primary)', marginBottom: 8, boxSizing: 'border-box' }}
                />
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Сопроводительное сообщение (необязательно)..."
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
                />
                <button
                  onClick={() => void sendReviewEmail()}
                  disabled={sending || !email.trim()}
                  style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: sending || !email.trim() ? 'not-allowed' : 'pointer', opacity: sending || !email.trim() ? 0.6 : 1 }}
                >
                  {sending ? 'Отправка...' : '✉ Отправить ссылку'}
                </button>
              </>
            )}
          </div>

          {/* Approval status */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Статус одобрения</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {(['pending', 'approved', 'changes_requested'] as ApprovalStatus[]).map(s => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  style={{
                    flex: 1, padding: '8px 6px', border: `2px solid ${status === s ? STATUS_COLORS[s] : 'var(--border)'}`,
                    borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    background: status === s ? STATUS_COLORS[s] + '20' : 'var(--bg-2)',
                    color: status === s ? STATUS_COLORS[s] : 'var(--text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {s === 'pending' ? '⏳' : s === 'approved' ? '✓' : '↩'} {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            {status !== 'pending' && (
              <textarea
                value={approvalNote}
                onChange={e => setApprovalNote(e.target.value)}
                placeholder={status === 'approved' ? 'Комментарий к одобрению...' : 'Описание запрошенных правок...'}
                rows={2}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-2)', color: 'var(--text-primary)', resize: 'vertical', boxSizing: 'border-box' }}
              />
            )}
            <div style={{ marginTop: 8, padding: '8px 12px', background: STATUS_COLORS[status] + '15', border: `1px solid ${STATUS_COLORS[status]}40`, borderRadius: 8 }}>
              <span style={{ fontSize: 12, color: STATUS_COLORS[status], fontWeight: 600 }}>
                {status === 'approved' ? '✓ Проект одобрен. Можно деплоить в production.' :
                 status === 'changes_requested' ? '↩ Клиент запросил правки. Внесите изменения и отправьте ссылку снова.' :
                 '⏳ Ожидаем ответа от клиента. Ссылка отправлена.'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 20px', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}
